import React, { useContext, useEffect, useState } from 'react'
import { Modal, Button, SelectField, TextField } from 'src/componentv2'
import { surveyType } from 'src/model'
import SurveyApiContext, { SurveyQuestion } from 'src/provider/API/v2/call-service/Survey'

export interface NewSurveyQuestionModalProps {
    open: boolean
    onClose: () => void
    ibInstitutionCode: number | string
    surveyTriggerId: number
    onSaved: () => void
    isEdit?: boolean
    initialQuestion?: SurveyQuestion
}

interface QuestionForm {
    questionType: string
    questionText: string
}

const EMPTY: QuestionForm = { questionType: '', questionText: '' }

export const NewSurveyQuestionModal: React.FC<NewSurveyQuestionModalProps> = ({
    open, onClose, ibInstitutionCode, surveyTriggerId, onSaved, isEdit = false, initialQuestion,
}) => {
    const { saveSurveyQuestion } = useContext(SurveyApiContext)
    const [form, setForm] = useState<QuestionForm>(EMPTY)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setForm(initialQuestion ? {
            questionType: surveyType.find((option) => option.label === initialQuestion.surveyType)?.value ?? '',
            questionText: initialQuestion.questionText,
        } : EMPTY)
    }, [open, initialQuestion])

    const isValid = form.questionType !== '' && form.questionText.trim().length > 0

    const handleSave = async () => {
        setSaving(true)
        try {
            const payload = {
                ibInstitutionCode,
                ...(isEdit && initialQuestion ? { id: initialQuestion.id } : {}),
                questionText: form.questionText,
                surveyType: Number(form.questionType),
                surveyTriggerID: surveyTriggerId,
            }

            const result = await saveSurveyQuestion(payload)
            if (result) {
                onSaved()
                onClose()
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? 'Edit Survey Question' : 'Add New Survey Question'}
            maxWidth="48rem"
        >
            <SelectField
                label="Question Type"
                placeholder="Select question type"
                value={form.questionType}
                onChange={(value) => setForm((f) => ({ ...f, questionType: value }))}
                options={surveyType.map((option) => ({ label: option.label, value: option.value }))}
                required
            />

            <div style={{ marginTop: '1.6rem' }}>
                <TextField
                    label="Question Text"
                    placeholder="Enter question text"
                    value={form.questionText}
                    onChange={(value) => setForm((f) => ({ ...f, questionText: value }))}
                    multiline
                    required
                />
            </div>

            <div style={{ marginTop: '2.4rem', display: 'flex', justifyContent: 'flex-end', gap: '1.2rem' }}>
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button disabled={!isValid || saving} onClick={handleSave}>
                    {saving ? 'Saving...' : isEdit ? 'Update Question' : 'Add Question'}
                </Button>
            </div>
        </Modal>
    )
}

export default NewSurveyQuestionModal
